import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RegistrationView } from "./views";
import { useTopicsData } from "./hooks/useTopicsData";
import { useAdsData } from "./hooks/useAdsData";
import { useUserSession } from "./hooks/useUserSession";
import { OPEN_DM_NOTIFICATION_EVENT, useUnreadDmCount } from "./hooks/useUnreadDmCount";
import { useNotifications } from "./hooks/useNotifications";
import { useAutoSignDmKey } from "./hooks/useAutoSignDmKey";
import { MainRoute } from "./routes/MainRoute";
import { SecondaryRoute } from "./routes/SecondaryRoute";
import { MiniProfileProvider } from "./lib/miniProfileContext";
import { trackLaunchOpen } from "./lib/launchAnalytics";
import { signedPost } from "./lib/signedRequest";
import { RetentionNotice } from "./components/RetentionNotice";
import { PublicChatGate, LegalDocuments, PublicLegalPage, hasAcceptedPublicChatTerms } from "./components/LegalExperience";
import "./dm-theme.css";

const SECONDARY_ROUTES = ["profile", "moderation", "messages", "notifications", "network", "admin"];
const PUBLIC_LEGAL_ROUTES = {
  "/privacy": "privacy",
  "/privacy/": "privacy",
  "/terms": "terms",
  "/terms/": "terms",
  "/community-rules": "rules",
  "/community-rules/": "rules",
};

function readPublicLegalDocument() {
  if (typeof window === "undefined") return null;
  const pathname = window.location.pathname.replace(/\/+$/, "") || "/";
  return PUBLIC_LEGAL_ROUTES[pathname] || null;
}

function readSharedMessageTarget() {
  if (typeof window === "undefined") return null;
  try {
    const url = new URL(window.location.href);
    const postId = String(url.searchParams.get("post") || "").trim();
    const topicSlug = String(url.searchParams.get("topic") || "").trim();
    if (!postId) return null;
    return { postId, topicSlug };
  } catch {
    return null;
  }
}

function App() {
  const topics = useTopicsData();
  const { heroAd, feedAds, swapHeroAd, swapFeedAdAt } = useAdsData();
  const { route, setRoute, user, completeRegistration, acknowledgeRetentionNotice, logout, deleteProfile } = useUserSession();
  const [activeTopic, setActiveTopic] = useState(null);
  const [activeTab, setActiveTab] = useState("recenti");
  const [profileIdentityId, setProfileIdentityId] = useState(null);
  const [dmInitialPeerId, setDmInitialPeerId] = useState(null);
  const [dmReturnState, setDmReturnState] = useState({ route: "home", scrollY: 0 });
  const [showPublicChatGate, setShowPublicChatGate] = useState(false);
  const [legalDocument, setLegalDocument] = useState(null);
  const [sharedMessageTarget, setSharedMessageTarget] = useState(readSharedMessageTarget);
  const publicLegalDocument = readPublicLegalDocument();
  const browserHistoryReady = useRef(false);
  const restoringBrowserHistory = useRef(false);
  const lastBrowserView = useRef("");

  const { count: unreadDm, refresh: refreshUnread } = useUnreadDmCount();
  const { recentCount: notifCount } = useNotifications();
  useAutoSignDmKey(user);

  // Apre/rinnova la sessione pubblica HttpOnly una volta per identità.
  // Le normali API protette, inclusi i checkout Stripe, possono poi
  // autenticare l'utente tramite cookie senza richiedere una nuova firma.
  useEffect(() => {
    if (!user?.identityId || typeof window === "undefined") return;

    const key = `ol_public_session_bootstrap_${user.identityId}`;

    if (window.sessionStorage.getItem(key) === "ok") return;

    let active = true;

    signedPost("/api/v1/auth/session", {})
      .then(() => {
        if (active) {
          window.sessionStorage.setItem(key, "ok");
        }
      })
      .catch((error) => {
        console.warn("Sessione pubblica non inizializzata:", error);
      });

    return () => {
      active = false;
    };
  }, [user?.identityId]);

  // Conteggio anonimo e best-effort di una sola apertura per sessione browser.
  // Le pagine legali pubbliche sono escluse e qualsiasi errore viene ignorato.
  useEffect(() => {
    if (!publicLegalDocument) trackLaunchOpen();
  }, [publicLegalDocument]);

  // Mantiene la navigazione interna nella cronologia del browser. Su Android
  // il tasto fisico Indietro genera un evento popstate: ripristiniamo quindi
  // la schermata precedente dell'app invece di lasciare subito il sito.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const view = { route, activeTopic: activeTopic || null };
    const key = `${view.route}::${view.activeTopic || ""}`;

    if (!browserHistoryReady.current) {
      window.history.replaceState(
        { ...(window.history.state || {}), opinioneLiberaView: view },
        "",
        window.location.href,
      );
      browserHistoryReady.current = true;
      lastBrowserView.current = key;
      return;
    }

    if (restoringBrowserHistory.current) {
      restoringBrowserHistory.current = false;
      lastBrowserView.current = key;
      return;
    }

    if (lastBrowserView.current === key) return;
    window.history.pushState(
      { ...(window.history.state || {}), opinioneLiberaView: view },
      "",
      window.location.href,
    );
    lastBrowserView.current = key;
  }, [activeTopic, route]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onPopState = (event) => {
      const view = event.state?.opinioneLiberaView;
      if (!view || typeof view.route !== "string") return;
      restoringBrowserHistory.current = true;
      setLegalDocument(null);
      setShowPublicChatGate(false);
      setActiveTopic(typeof view.activeTopic === "string" && view.activeTopic ? view.activeTopic : null);
      setRoute(view.route);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [setRoute]);

  const sharedTopic = useMemo(() => {
    if (!sharedMessageTarget?.topicSlug || !Array.isArray(topics)) return null;
    return topics.find((topic) => topic.slug === sharedMessageTarget.topicSlug) || null;
  }, [sharedMessageTarget, topics]);


  // Un link esterno resta nell'URL durante registrazione o recupero. Appena
  // esiste un profilo, apre l'argomento indicato senza modificare gli altri
  // flussi di navigazione dell'app.
  useEffect(() => {
    if (!user || !sharedMessageTarget?.topicSlug) return;
    setRoute("home");
    setActiveTab("recenti");
    setActiveTopic(sharedMessageTarget.topicSlug);
    if (!hasAcceptedPublicChatTerms(user.identityId)) setShowPublicChatGate(true);
  }, [sharedMessageTarget, setRoute, user]);

  // Attende che il feed reale abbia terminato il caricamento, quindi porta al
  // messaggio richiesto e lo evidenzia. Non altera ordinamento, dati o azioni.
  useEffect(() => {
    if (!user || !sharedMessageTarget?.postId || showPublicChatGate) return undefined;
    if (sharedMessageTarget.topicSlug && activeTopic !== sharedMessageTarget.topicSlug) return undefined;

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      const selector = `[data-testid="post-${CSS.escape(sharedMessageTarget.postId)}"]`;
      const postElement = document.querySelector(selector);
      if (postElement) {
        window.clearInterval(timer);
        postElement.scrollIntoView({ behavior: "smooth", block: "center" });
        postElement.classList.add("ring-4", "ring-primary", "ring-offset-2", "rounded-2xl");
        window.setTimeout(() => {
          postElement.classList.remove("ring-4", "ring-primary", "ring-offset-2", "rounded-2xl");
        }, 5000);
        setSharedMessageTarget(null);
      } else if (attempts >= 40) {
        window.clearInterval(timer);
      }
    }, 250);

    return () => window.clearInterval(timer);
  }, [activeTopic, sharedMessageTarget, showPublicChatGate, user]);


  const goHome = useCallback(() => {
    setActiveTopic(null);
    setRoute("home");
  }, [setRoute]);
  const openProfile = useCallback(() => {
    setProfileIdentityId(user?.identityId || null);
    setRoute("profile");
  }, [setRoute, user?.identityId]);

  const openProfileById = useCallback((identityId) => {
    if (!identityId) return;
    setProfileIdentityId(identityId);
    setRoute("profile");
  }, [setRoute]);
  const openMessages = useCallback(
    (peerId) => {
      setDmInitialPeerId(typeof peerId === "string" ? peerId : null);
      if (route !== "messages") {
        setDmReturnState({
          route: SECONDARY_ROUTES.includes(route) ? route : "home",
          scrollY: typeof window !== "undefined" ? window.scrollY : 0,
        });
      }
      setRoute("messages");
      setTimeout(() => refreshUnread(), 1500);
    }, [route, setRoute, refreshUnread],
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleDmNotificationClick = (event) => {
      const peerId = event.detail?.peerId;
      if (typeof peerId === "string" && peerId) openMessages(peerId);
    };
    window.addEventListener(OPEN_DM_NOTIFICATION_EVENT, handleDmNotificationClick);
    return () => window.removeEventListener(OPEN_DM_NOTIFICATION_EVENT, handleDmNotificationClick);
  }, [openMessages]);

  const openNotificationMessage = useCallback((notification) => {
    const postId = notification?.type === "reply"
      ? (notification.parentPostId || notification.postId)
      : notification?.postId;
    const topicSlug = notification?.topicSlug;
    if (!postId || !topicSlug) return;
    setSharedMessageTarget({ postId: String(postId), topicSlug: String(topicSlug) });
  }, []);

  const closeMessages = useCallback(() => {
    const returnRoute = dmReturnState.route === "messages" ? "home" : (dmReturnState.route || "home");
    setRoute(returnRoute);
    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => window.scrollTo({ top: dmReturnState.scrollY || 0, left: 0, behavior: "auto" }));
      });
    }
  }, [dmReturnState, setRoute]);
  const openNotifications = useCallback(() => setRoute("notifications"), [setRoute]);
  const openNetwork = useCallback(() => setRoute("network"), [setRoute]);
  const openAdmin = useCallback(() => setRoute("admin"), [setRoute]);
  const goBackToHomeTopic = useCallback(() => setActiveTopic(null), []);
  const selectTopic = useCallback((topic) => {
    setActiveTopic(topic);
    if (!hasAcceptedPublicChatTerms(user?.identityId)) setShowPublicChatGate(true);
  }, [user?.identityId]);

  let appContent;
  if (route === "loading") {
    appContent = <div className="min-h-screen bg-cream flex items-center justify-center text-sm font-semibold text-muted">Apertura del profilo…</div>;
  } else if (route === "register") {
    appContent = <RegistrationView onComplete={completeRegistration} />;
  } else if (route === "retention-notice") {
    appContent = <RetentionNotice onAccept={acknowledgeRetentionNotice} />;
  } else {
    const displayUser = user;
    appContent = (
      <MiniProfileProvider onOpenProfileById={openProfileById}>
        {SECONDARY_ROUTES.includes(route) ? (
          <SecondaryRoute
            route={route}
            user={displayUser}
            onGoHome={goHome}
            onOpenProfile={openProfile}
          onOpenMessages={openMessages}
            onSetRoute={setRoute}
            onCloseMessages={closeMessages}
            onLogout={logout}
            onDeleteProfile={deleteProfile}
            unreadDm={unreadDm}
            notifCount={notifCount}
            dmInitialPeerId={dmInitialPeerId}
            profileIdentityId={profileIdentityId}
            onOpenNetwork={openNetwork}
            onOpenLegal={setLegalDocument}
            onOpenAdmin={openAdmin}
            onOpenNotification={openNotificationMessage}
          />
        ) : (
          <MainRoute
            topics={topics}
            heroAd={heroAd}
            onSwapHero={swapHeroAd}
            activeTopic={activeTopic}
            onSelectTopic={selectTopic}
            onBackHome={goBackToHomeTopic}
            feedAds={feedAds}
            onSwapFeedAd={swapFeedAdAt}
            activeTab={activeTab}
            onChangeTab={setActiveTab}
            user={displayUser}
            onOpenProfile={openProfile}
            onGoHome={goHome}
            onOpenMessages={openMessages}
            onOpenNotifications={openNotifications}
            onOpenNetwork={openNetwork}
            onOpenLegal={setLegalDocument}
            onOpenAdmin={openAdmin}
            unreadDm={unreadDm}
            notifCount={notifCount}
          />
        )}
      </MiniProfileProvider>
    );
  }

  if (publicLegalDocument) return <PublicLegalPage initial={publicLegalDocument} />;

  return (
    <>
      {appContent}
      {showPublicChatGate && activeTopic && (
        <PublicChatGate
          identityId={user?.identityId}
          topicTitle={sharedTopic?.title || activeTopic?.title || "questo argomento"}
          onAccepted={() => setShowPublicChatGate(false)}
          onOpenDocument={setLegalDocument}
        />
      )}
      {legalDocument && <LegalDocuments initial={legalDocument} onClose={() => setLegalDocument(null)} />}
    </>
  );
}

export default App;
